// API configuration and client functions
import type { Brand, Product, CollectionResponse, ErrorResponse } from '@/types/product';
import { addSentryBreadcrumb, captureError } from "@/lib/sentry";

// Production API URL (base URL from wrangler.jsonc API_URL + /v1 suffix)
const PRODUCTION_API_URL = 'https://obelisk-prod-521448797761.us-east4.run.app/v1';

// Custom domain for OAuth callbacks - registered with OAuth providers (TikTok, etc.)
// INTENTIONALLY HARDCODED: This is infrastructure configuration that must match:
// 1. TikTok Developer Console registered callback URL
// 2. Backend api_base_url in terraform/prod.tfvars
// Using an environment variable would require VITE_ prefix for client-side access,
// adding complexity for a value that changes only during infrastructure migrations.
const CUSTOM_API_DOMAIN = 'https://api.obeliskapp.io';

// Get the API base URL based on environment
// Uses import.meta.env.DEV for consistent dev detection across all hostnames
// (localhost, 127.0.0.1, custom local domains, etc.)
export function getApiUrl(): string {
  return import.meta.env.DEV
    ? 'http://localhost:8080/v1'  // Development API
    : PRODUCTION_API_URL;
}

/**
 * Get API URL for OAuth callbacks.
 *
 * OAuth providers (especially TikTok) require callbacks to be registered domains.
 * TikTok's Developer Console has https://api.obeliskapp.io/v1/tiktok/callback registered,
 * NOT the Cloud Run URL. Using the wrong URL causes "redirect_uri validation failed".
 *
 * Uses import.meta.env.DEV for consistent dev detection across all hostnames
 * (localhost, 127.0.0.1, custom local domains, etc.)
 *
 * @returns Custom domain URL in production, localhost in development
 */
export function getOAuthApiUrl(): string {
  return import.meta.env.DEV
    ? 'http://localhost:8080/v1'  // Development API
    : `${CUSTOM_API_DOMAIN}/v1`;
}

// API Error class
export class ApiError extends Error {
  status: number;
  error?: ErrorResponse['error'];

  constructor(message: string, status: number, error?: ErrorResponse['error']) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.error = error;
  }
}


// Token management
export const tokenManager = {
  getToken(): string | null {
    // In a real app, this would get the token from secure storage
    // For now, we'll use localStorage as a placeholder
    if (typeof window !== 'undefined') {
      return localStorage.getItem('auth_token');
    }
    return null;
  },

  setToken(token: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth_token', token);
    }
  },

  clearToken(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_token');
    }
  }
};

// Generic API client with JSON:API support
class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getApiUrl();
  }

  private getHeaders(explicitToken?: string | null): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    // Use explicit token if provided, otherwise fall back to tokenManager
    const token = explicitToken !== undefined ? explicitToken : tokenManager.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      if (import.meta.env.DEV) {
        console.debug('[API] Token present:', true);
      }
    } else if (import.meta.env.DEV) {
      console.warn('[API] No token available for request!');
    }

    return headers;
  }

  private async handleResponse<T>(response: Response, endpoint: string, method: string): Promise<T> {
    const contentType = response.headers.get('content-type');
    const requestId = response.headers.get('x-request-id');

    if (!response.ok) {
      let errorData: ErrorResponse | null = null;

      if (contentType?.includes('application/json')) {
        try {
          errorData = await response.json();
        } catch {
          // If JSON parsing fails, continue with generic error
        }
      }

      const error = new ApiError(
        errorData?.error?.message || `API Error: ${response.status} ${response.statusText}`,
        response.status,
        errorData?.error
      );

      // Capture server errors (5xx) to Sentry with context
      // Don't capture 4xx errors as they're typically user errors
      if (response.status >= 500) {
        captureError(error, {
          tags: {
            "api.endpoint": endpoint,
            "api.method": method,
            "api.status": response.status.toString(),
            ...(requestId && { "api.request_id": requestId }),
          },
          extra: {
            endpoint,
            method,
            status: response.status,
            statusText: response.statusText,
            requestId,
            errorCode: errorData?.error?.code,
            errorMessage: errorData?.error?.message,
          },
          level: "error",
        });
      }

      // Add breadcrumb for API errors (4xx and 5xx)
      // Note: Success breadcrumbs are handled by breadcrumbsIntegration({ fetch: true })
      addSentryBreadcrumb(
        "api",
        `${method} ${endpoint} failed with ${response.status}`,
        { endpoint, method, status: response.status, requestId },
        response.status >= 500 ? "error" : "warning"
      );

      throw error;
    }

    // Success breadcrumbs are automatically captured by Sentry's
    // breadcrumbsIntegration({ fetch: true }) in entry.client.tsx

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  async get<T = any>(endpoint: string, params?: Record<string, string>, token?: string | null): Promise<T> {
    const url = new URL(`${this.baseUrl}${endpoint}`);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    if (import.meta.env.DEV) {
      console.debug('[API] GET', endpoint, 'token provided:', !!token);
    }
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.getHeaders(token),
    });

    return this.handleResponse<T>(response, endpoint, 'GET');
  }

  async post<T = any>(endpoint: string, data?: unknown, token?: string | null): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(token),
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response, endpoint, 'POST');
  }

  async patch<T = any>(endpoint: string, data?: unknown, token?: string | null): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PATCH',
      headers: this.getHeaders(token),
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response, endpoint, 'PATCH');
  }

  async delete<T = any>(endpoint: string, token?: string | null): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers: this.getHeaders(token),
    });

    return this.handleResponse<T>(response, endpoint, 'DELETE');
  }
}

export const apiClient = new ApiClient();

// Export the base URL getter for services that need direct fetch access
export function getApiBaseUrl(): string {
  return getApiUrl();
}
